<template>
  <div class="lock-validations-page">
    <template v-if="isAuthorized">
      <div class="page-header">
        <PageHeaderPortal>
          <div class="page-header-left">
            <div class="page-icon">
              <i class="fa-duotone fa-shield-check"></i>
            </div>

            <div>
              <h1 class="page-title">Validations</h1>
              <p class="page-subtitle">How long locks sit unvalidated, and why</p>
            </div>
          </div>
        </PageHeaderPortal>

        <div class="header-actions">
          <!-- The window applies to completed validations. Loans unvalidated right now are always
               listed, however long they have been waiting. -->
          <select v-model.number="days" class="filter-select days-select" @change="loadData">
            <option :value="7">Validated last 7 days</option>
            <option :value="14">Validated last 14 days</option>
            <option :value="30">Validated last 30 days</option>
            <option :value="60">Validated last 60 days</option>
            <option :value="90">Validated last 90 days</option>
            <option :value="180">Validated last 180 days</option>
            <option :value="365">Validated last 365 days</option>
          </select>

          <button class="export-btn" :disabled="loading || !exportRows.length" @click="exportCsv">
            Export CSV
          </button>

          <button class="refresh-btn" :disabled="loading" @click="loadData">
            {{ loading ? 'Loading...' : 'Refresh' }}
          </button>
        </div>
      </div>

      <div v-if="errorMessage" class="page-error">{{ errorMessage }}</div>

      <!-- Tracking began when this page went live, and a loan enters on its next Encompass save.
           One that was already unvalidated by then has no known start, so its time is a floor. -->
      <div v-if="unknownStartCount" class="panel-hint">
        <i class="fa-duotone fa-circle-info"></i>
        <div>
          <strong>{{ unknownStartCount }}</strong> of these started before tracking: the loan was
          already unvalidated the first time it was seen, so when it went unvalidated is unknown.
          They are marked <span class="since-chip">before tracking</span>, their times are minimums,
          and they are left out of every average on this page.
        </div>
      </div>

      <div class="tab-bar" role="tablist">
        <button class="tab-btn"
                role="tab"
                :class="{ 'is-on': tab === 'open' }"
                :aria-selected="tab === 'open'"
                @click="tab = 'open'">
          Unvalidated now
          <span class="tab-btn-count">{{ openRows.length }}</span>
        </button>
        <button class="tab-btn"
                role="tab"
                :class="{ 'is-on': tab === 'history' }"
                :aria-selected="tab === 'history'"
                @click="tab = 'history'">
          History
          <span class="tab-btn-count">{{ historyRows.length }}</span>
        </button>
        <button class="tab-btn"
                role="tab"
                :class="{ 'is-on': tab === 'reasons' }"
                :aria-selected="tab === 'reasons'"
                @click="tab = 'reasons'">
          By reason
        </button>
      </div>

      <!-- ── Unvalidated now ─────────────────────────────────────────────── -->
      <template v-if="tab === 'open'">
        <div class="stat-grid">
          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-shield-exclamation stat-icon"></i>
              <span class="stat-label">Unvalidated now</span>
            </div>
            <div class="stat-value">{{ openSummary.total }}</div>
            <div class="stat-foot">{{ openSummary.branches }} branches</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-hourglass-end stat-icon"></i>
              <span class="stat-label">Oldest</span>
            </div>
            <div class="stat-value">{{ openSummary.oldest ? ageText(openSummary.oldest) : '—' }}</div>
            <div class="stat-foot">{{ openSummary.oldest ? 'loan ' + (openSummary.oldest.loan_number || 'not in reporting') : 'nothing waiting' }}</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-stopwatch stat-icon"></i>
              <span class="stat-label">Median time open</span>
            </div>
            <div class="stat-value">{{ openSummary.median === null ? '—' : fmtDuration(openSummary.median) }}</div>
            <div class="stat-foot">{{ openSummary.timed }} with a known start</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-list-check stat-icon"></i>
              <span class="stat-label">Most common reason</span>
            </div>
            <div class="stat-value stat-value-text">{{ openSummary.topReason ? openSummary.topReason.label : '—' }}</div>
            <div class="stat-foot">
              {{ openSummary.topReason ? 'on ' + openSummary.topReason.count + ' of these loans' : 'no reasons recorded' }}
            </div>
          </div>
        </div>
      </template>

      <!-- ── History ─────────────────────────────────────────────────────── -->
      <template v-else-if="tab === 'history'">
        <div class="stat-grid">
          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-circle-check stat-icon"></i>
              <span class="stat-label">Validated</span>
            </div>
            <div class="stat-value">{{ historySummary.total }}</div>
            <div class="stat-foot">{{ historySummary.loans }} loans · last {{ days }} days</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-stopwatch stat-icon"></i>
              <span class="stat-label">Median time to validate</span>
            </div>
            <div class="stat-value">{{ historySummary.median === null ? '—' : fmtDuration(historySummary.median) }}</div>
            <div class="stat-foot">{{ historySummary.timed }} with a known start</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-gauge-high stat-icon"></i>
              <span class="stat-label">90th percentile</span>
            </div>
            <div class="stat-value">{{ historySummary.p90 === null ? '—' : fmtDuration(historySummary.p90) }}</div>
            <div class="stat-foot">9 in 10 were validated within this</div>
          </div>

          <div class="stat-card">
            <div class="stat-head">
              <i class="fa-duotone fa-scale-balanced stat-icon"></i>
              <span class="stat-label">Average</span>
            </div>
            <div class="stat-value">{{ historySummary.mean === null ? '—' : fmtDuration(historySummary.mean) }}</div>
            <div class="stat-foot">
              {{ historySummary.longest === null ? '' : 'longest ' + fmtDuration(historySummary.longest) }}
            </div>
          </div>

          <div class="stat-card" :class="{ 'stat-muted': !historySummary.unknownStart }">
            <div class="stat-head">
              <i class="fa-duotone fa-clock-rotate-left stat-icon"></i>
              <span class="stat-label">Before tracking</span>
            </div>
            <div class="stat-value">{{ historySummary.unknownStart }}</div>
            <div class="stat-foot">start unknown, not averaged</div>
          </div>
        </div>
      </template>

      <!-- ── By reason ───────────────────────────────────────────────────── -->
      <section v-if="tab === 'reasons'" class="panel table-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <i class="fa-duotone fa-list-check panel-icon"></i>
            <span class="panel-title">What sends a lock to validation</span>
          </div>
          <span class="panel-note">loans unvalidated now, plus validations in the last {{ days }} days</span>
        </div>

        <div class="table-wrap">
          <table class="data-table sortable-table">
            <thead>
              <tr>
                <th @click="sort('number')">Reason {{ sortMark('number') }}</th>
                <th @click="sort('tripped')">Times tripped {{ sortMark('tripped') }}</th>
                <th class="num" @click="sort('loans')">Loans {{ sortMark('loans') }}</th>
                <th class="num" @click="sort('openNow')">Open now {{ sortMark('openNow') }}</th>
                <th class="num"
                    title="How long the reason stayed tripped, over the ones that have cleared."
                    @click="sort('median')">Median time tripped {{ sortMark('median') }}</th>
                <th class="num" @click="sort('mean')">Average {{ sortMark('mean') }}</th>
                <th class="num" @click="sort('longest')">Longest {{ sortMark('longest') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="7" class="table-state">Loading...</td>
              </tr>
              <tr v-else-if="!reasonRows.length">
                <td colspan="7" class="table-state">No reasons recorded yet.</td>
              </tr>
              <tr v-for="r in reasonRows" :key="r.number">
                <td>
                  <span class="reason-number">{{ r.number }}</span>
                  <span class="strong">{{ r.label }}</span>
                </td>
                <td class="bar-cell">
                  <div class="mix-bar">
                    <div class="mix-bar-fill" :style="{ width: r.share + '%' }"></div>
                  </div>
                  <span class="bar-count">{{ r.tripped }}</span>
                </td>
                <td class="num">{{ r.loans }}</td>
                <td class="num" :class="{ strong: r.openNow > 0 }">{{ r.openNow }}</td>
                <td class="num">{{ r.median === null ? '—' : fmtDuration(r.median) }}</td>
                <td class="num">{{ r.mean === null ? '—' : fmtDuration(r.mean) }}</td>
                <td class="num">{{ r.longest === null ? '—' : fmtDuration(r.longest) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ── Loan list (Unvalidated now / History) ───────────────────────── -->
      <section v-else class="panel table-panel">
        <div class="panel-header">
          <div class="panel-title-wrap">
            <i class="fa-duotone fa-table-list panel-icon"></i>
            <span class="panel-title">{{ tab === 'open' ? 'Loans unvalidated now' : 'Validations completed' }}</span>
          </div>
          <span class="panel-count">{{ listRows.length }} of {{ tab === 'open' ? openEpisodes.length : closedEpisodes.length }}</span>
        </div>

        <div class="toolbar">
          <input v-model.trim="search"
                 class="search-input"
                 type="text"
                 placeholder="Search loan, borrower, LO or branch" />

          <select v-model.number="reasonFilter" class="filter-select">
            <option :value="0">All reasons</option>
            <option v-for="opt in reasonOptions" :key="opt.number" :value="opt.number">{{ opt.label }}</option>
          </select>

          <select v-model="channelFilter" class="filter-select">
            <option value="">All channels</option>
            <option v-for="opt in channelOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <select v-model="branchFilter" class="filter-select">
            <option value="">All branches</option>
            <option v-for="opt in branchOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>

          <!-- A loan that closes or withdraws while unvalidated never gets a save that validates it,
               so it would sit here forever. Hidden by default, one click away. -->
          <label v-if="tab === 'open'" class="toggle-inline">
            <input v-model="showInactive" type="checkbox" />
            Include inactive loans
            <span v-if="inactiveOpenCount" class="toggle-count">{{ inactiveOpenCount }}</span>
          </label>
        </div>

        <div class="table-wrap">
          <table class="data-table sortable-table loans-table">
            <thead>
              <tr>
                <th class="expand-col"></th>
                <th v-if="tab === 'history'" @click="sort('closedMs')">Validated {{ sortMark('closedMs') }}</th>
                <th @click="sort('loan_number')">Loan {{ sortMark('loan_number') }}</th>
                <th @click="sort('borrowerName')">Borrower {{ sortMark('borrowerName') }}</th>
                <th @click="sort('loan_officer_name')">Loan officer {{ sortMark('loan_officer_name') }}</th>
                <th @click="sort('orgid')">Branch {{ sortMark('orgid') }}</th>
                <th @click="sort('channel')">Channel {{ sortMark('channel') }}</th>
                <th v-if="tab === 'open'" @click="sort('current_milestone')">Milestone {{ sortMark('current_milestone') }}</th>
                <th @click="sort('openedMs')">Unvalidated {{ sortMark('openedMs') }}</th>
                <th class="num" @click="sort('seconds')">{{ tab === 'open' ? 'Open for' : 'Time to validate' }} {{ sortMark('seconds') }}</th>
                <th @click="sort('reasonCount')">Reasons {{ sortMark('reasonCount') }}</th>
                <th v-if="tab === 'open'" @click="sort('lockExpiresMs')">Lock expires {{ sortMark('lockExpiresMs') }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="loading">
                <td :colspan="columnCount" class="table-state">Loading validations...</td>
              </tr>

              <tr v-else-if="!listRows.length">
                <td :colspan="columnCount" class="table-state">
                  {{ tab === 'open'
                      ? (openEpisodes.length ? 'No unvalidated loans match these filters.' : 'No loans are unvalidated right now.')
                      : (closedEpisodes.length ? 'No validations match these filters.' : 'No validations completed in this window.') }}
                </td>
              </tr>

              <template v-for="row in pagedRows" :key="row.id">
                <tr class="loan-row" :class="{ 'is-open': expandedId === row.id }" @click="toggleExpand(row)">
                  <td class="expand-col">
                    <i class="fa-solid" :class="expandedId === row.id ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                  </td>
                  <td v-if="tab === 'history'">{{ formatDateTime(row.closed_utc) }}</td>
                  <td class="mono strong">{{ row.loan_number || '—' }}</td>
                  <td>{{ row.borrowerName || '—' }}</td>
                  <td>{{ row.loan_officer_name || '—' }}</td>
                  <td>{{ row.orgid || '—' }}</td>
                  <td>{{ row.channel || '—' }}</td>
                  <td v-if="tab === 'open'">
                    {{ row.current_milestone || '—' }}
                    <span v-if="row.isInactive" class="status-chip">{{ row.loan_status }}</span>
                  </td>
                  <td>
                    {{ formatDateTime(row.opened_utc) }}
                    <span v-if="!row.start_known" class="since-chip" :title="unknownStartTitle">before tracking</span>
                  </td>
                  <td class="num strong">{{ ageText(row) }}</td>
                  <td>
                    <span v-if="!rowReasons(row).length" class="reason-pill reason-none">none recorded</span>
                    <span v-for="r in rowReasons(row).slice(0, 3)"
                          :key="r.id"
                          class="reason-pill"
                          :class="{ 'reason-cleared': tab === 'open' && !r.isOpen }">{{ r.reason_label }}</span>
                    <span v-if="rowReasons(row).length > 3" class="reason-more">+{{ rowReasons(row).length - 3 }}</span>
                  </td>
                  <td v-if="tab === 'open'" :class="{ 'lock-expired': row.lockExpired }">
                    {{ formatDay(row.lock_expires) }}
                  </td>
                </tr>

                <tr v-if="expandedId === row.id" :key="row.id + '-detail'" class="detail-row">
                  <td :colspan="columnCount">
                    <div class="detail-grid">
                      <div class="detail-block detail-wide">
                        <div class="sub-title">Reasons</div>
                        <div v-if="!row.reasons.length" class="detail-empty">
                          No CX.LV.CHANGE reason was Yes while this loan was unvalidated, or the
                          Encompass read that records them failed.
                        </div>
                        <table v-else class="data-table reason-table">
                          <thead>
                            <tr>
                              <th>Reason</th>
                              <th>At lock → now</th>
                              <th>Tripped</th>
                              <th>Cleared</th>
                              <th class="num">Tripped for</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="r in row.reasons" :key="r.id">
                              <td class="strong">{{ r.reason_label }}</td>
                              <td>
                                <template v-if="r.locked_value || r.current_value">
                                  <span class="value-old">{{ r.locked_value || 'blank' }}</span>
                                  <i class="fa-solid fa-arrow-right value-arrow"></i>
                                  <span class="value-new">{{ r.current_value || 'blank' }}</span>
                                </template>
                                <span v-else class="muted">—</span>
                              </td>
                              <td>{{ formatDateTime(r.tripped_utc) }}</td>
                              <td>
                                <template v-if="r.isOpen">
                                  <span class="reason-pill">still Yes</span>
                                </template>
                                <template v-else>
                                  {{ formatDateTime(r.cleared_utc) }}
                                  <span class="muted">{{ r.cleared_by === 'validated' ? '(validated)' : '(went back to No)' }}</span>
                                </template>
                              </td>
                              <td class="num">{{ fmtDuration(reasonSeconds(r)) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      <div class="detail-block">
                        <div class="sub-title">The loan</div>
                        <dl class="detail-list">
                          <div><dt>Loan status</dt><dd>{{ row.loan_status || '—' }}</dd></div>
                          <div><dt>Milestone</dt><dd>{{ row.current_milestone || '—' }}</dd></div>
                          <div><dt>Channel</dt><dd>{{ row.channel || '—' }}</dd></div>
                          <div><dt>Lock expires</dt><dd>{{ formatDay(row.lock_expires) }}</dd></div>
                          <div>
                            <dt>Unvalidated</dt>
                            <dd>{{ formatDateTime(row.opened_utc) }}<span v-if="!row.start_known"> or earlier</span></dd>
                          </div>
                          <div><dt>Validated</dt><dd>{{ row.isOpen ? 'not yet' : formatDateTime(row.closed_utc) }}</dd></div>
                          <div><dt>{{ row.isOpen ? 'Open for' : 'Time to validate' }}</dt><dd>{{ ageText(row) }}</dd></div>
                        </dl>

                        <div class="detail-links">
                          <a class="polly-btn"
                             :href="pollyUrl(row)"
                             target="_blank"
                             rel="noopener noreferrer"
                             @click.stop="openLoanLink(pollyUrl(row), $event)">
                            Open Polly
                          </a>
                          <a class="encompass-btn"
                             :href="encompassUrl(row)"
                             target="_blank"
                             rel="noopener noreferrer"
                             @click.stop="openLoanLink(encompassUrl(row), $event)">
                            Open Encompass
                          </a>
                        </div>
                      </div>

                      <div class="detail-block">
                        <div class="sub-title">Other times this loan was unvalidated</div>
                        <div v-if="!otherEpisodes(row).length" class="detail-empty">
                          No other time in the loaded window.
                        </div>
                        <dl v-else class="detail-list">
                          <div v-for="ep in otherEpisodes(row)" :key="ep.id">
                            <dt>{{ formatDate(ep.opened_utc) }}</dt>
                            <dd>
                              {{ ep.isOpen ? 'still open' : fmtDuration(ep.duration_seconds) }}
                              <span class="muted">· {{ ep.reasons.length }} reason{{ ep.reasons.length === 1 ? '' : 's' }}</span>
                            </dd>
                          </div>
                        </dl>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="pagination-bar">
          <div class="pagination-info">
            Showing {{ pagedRows.length }} of {{ listRows.length }}
            <span v-if="loadedAt"> · loaded {{ formatDateTime(loadedAt) }}</span>
          </div>
          <div class="pagination-actions">
            <button class="page-btn" :disabled="page <= 1" @click="page -= 1">Prev</button>
            <span class="page-number">Page {{ page }} of {{ totalPages }}</span>
            <button class="page-btn" :disabled="page >= totalPages" @click="page += 1">Next</button>
          </div>
        </div>
      </section>
    </template>

    <div v-else-if="!accessChecked" class="state-card">Loading validations...</div>

    <div v-else class="not-authorized-card">
      <div class="not-authorized-icon">
        <i class="fa-duotone fa-lock"></i>
      </div>
      <div class="not-authorized-title">Not authorized</div>
      <div class="not-authorized-text">{{ accessMessage }}</div>
    </div>
  </div>
</template>

<script>
  import PageHeaderPortal from "@/views/PageHeaderPortal.vue"
  import api from '@/api/api.service'
  import auth from "@/authConfig.js"
  import { openLoanInWindow } from '@/helpers/openLoanWindow.js'
  import { formatDuration } from '@/helpers/formatters.js'

  // Each tab's starting sort: the longest-waiting loan first, the latest validation first, the
  // most frequent reason first.
  const DEFAULT_SORTS = {
    open: { by: 'openedMs', dir: 'asc' },
    history: { by: 'closedMs', dir: 'desc' },
    reasons: { by: 'tripped', dir: 'desc' }
  }

  // Matches the worker's one-minute schedule: refreshing faster would only re-read the same rows.
  const REFRESH_MS = 60000

  export default {
    name: 'LockValidations',
    components: { PageHeaderPortal },
    data() {
      return {
        // Access is the API's answer (ConcessionsAdmin, checked server-side), never localStorage:
        // that role is not in active_user_roles, so the page could not check it if it tried.
        accessChecked: false,
        accessDenied: false,
        accessMessage: '',
        loading: false,
        errorMessage: '',
        episodes: [],
        days: 90,
        loadedAt: null,
        tab: 'open',
        search: '',
        reasonFilter: 0,
        channelFilter: '',
        branchFilter: '',
        showInactive: false,
        sorts: JSON.parse(JSON.stringify(DEFAULT_SORTS)),
        page: 1,
        pageSize: 50,
        expandedId: null,
        // Ticks so "open for" keeps counting while the page sits open.
        now: Date.now(),
        clock: null,
        // Background refresh. loadSeq lets a newer load win over a slower older one (a days change
        // while a refresh is in flight); loadInFlight keeps refreshes from stacking up.
        refreshTimer: null,
        loadSeq: 0,
        loadInFlight: false,
        // Polly's org id, as the queue and the extensions dashboard both use it for loan links.
        pollyOrgId: 698,
        unknownStartTitle: 'This loan was already unvalidated the first time it was seen, so when it went unvalidated is unknown. Its time is a minimum, and it is left out of averages.'
      }
    },
    computed: {
      isAuthorized() {
        return this.accessChecked && !this.accessDenied
      },
      openEpisodes() {
        return this.episodes.filter(e => e.isOpen)
      },
      closedEpisodes() {
        return this.episodes.filter(e => !e.isOpen)
      },
      episodesByLoan() {
        const map = new Map()
        for (const ep of this.episodes) {
          if (!map.has(ep.loan_id)) map.set(ep.loan_id, [])
          map.get(ep.loan_id).push(ep)
        }
        for (const list of map.values()) list.sort((a, b) => b.openedMs - a.openedMs)
        return map
      },
      unknownStartCount() {
        return this.episodes.filter(e => !e.start_known).length
      },
      inactiveOpenCount() {
        return this.openEpisodes.filter(e => e.isInactive).length
      },
      reasonOptions() {
        const seen = new Map()
        for (const ep of this.episodes) {
          for (const r of ep.reasons) {
            if (!seen.has(r.reason_number)) seen.set(r.reason_number, { number: r.reason_number, label: r.reason_label })
          }
        }
        return [...seen.values()].sort((a, b) => a.number - b.number)
      },
      channelOptions() {
        return [...new Set(this.episodes.map(e => e.channel).filter(Boolean))].sort()
      },
      branchOptions() {
        return [...new Set(this.episodes.map(e => e.orgid).filter(Boolean))]
          .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      },
      openRows() {
        return this.filterRows(this.openEpisodes, true)
      },
      historyRows() {
        return this.filterRows(this.closedEpisodes, false)
      },
      listRows() {
        const key = this.tab === 'history' ? 'history' : 'open'
        const rows = key === 'history' ? this.historyRows : this.openRows
        return [...rows].sort((a, b) => this.compareRows(a, b, this.sorts[key]))
      },
      columnCount() {
        return this.tab === 'open' ? 11 : 10
      },
      totalPages() {
        return Math.max(1, Math.ceil(this.listRows.length / this.pageSize))
      },
      pagedRows() {
        const start = (this.page - 1) * this.pageSize
        return this.listRows.slice(start, start + this.pageSize)
      },
      openSummary() {
        const rows = this.openRows
        // A before-tracking loan's age is only a floor, so it does not enter the median.
        const timed = rows.filter(r => r.start_known).map(r => this.episodeSeconds(r))
        const oldest = rows.length
          ? rows.reduce((a, b) => (a.openedMs <= b.openedMs ? a : b))
          : null

        const counts = new Map()
        for (const row of rows) {
          for (const r of row.reasons.filter(x => x.isOpen)) {
            const entry = counts.get(r.reason_number) || { label: r.reason_label, count: 0 }
            entry.count += 1
            counts.set(r.reason_number, entry)
          }
        }
        const topReason = [...counts.values()].sort((a, b) => b.count - a.count)[0] || null

        return {
          total: rows.length,
          branches: new Set(rows.map(r => r.orgid).filter(Boolean)).size,
          oldest,
          timed: timed.length,
          median: this.median(timed),
          topReason
        }
      },
      historySummary() {
        const rows = this.historyRows
        const timed = rows
          .filter(r => r.start_known)
          .map(r => r.duration_seconds)
          .filter(x => typeof x === 'number' && x >= 0)

        return {
          total: rows.length,
          loans: new Set(rows.map(r => r.loan_id)).size,
          timed: timed.length,
          median: this.median(timed),
          p90: this.percentile(timed, 0.9),
          mean: timed.length ? Math.round(timed.reduce((s, x) => s + x, 0) / timed.length) : null,
          longest: timed.length ? Math.max(...timed) : null,
          unknownStart: rows.filter(r => !r.start_known).length
        }
      },
      // Over every reason row the page holds: open episodes of any age plus the window's history.
      // Durations only count reasons that have cleared; the ones still tripped are "open now".
      reasonRows() {
        const groups = new Map()
        for (const ep of this.episodes) {
          for (const r of ep.reasons) {
            const g = groups.get(r.reason_number) || {
              number: r.reason_number,
              label: r.reason_label,
              tripped: 0,
              loanIds: new Set(),
              openNow: 0,
              durations: []
            }
            g.tripped += 1
            g.loanIds.add(ep.loan_id)
            if (r.isOpen) g.openNow += 1
            else if (typeof r.duration_seconds === 'number' && r.duration_seconds >= 0) g.durations.push(r.duration_seconds)
            groups.set(r.reason_number, g)
          }
        }

        const list = [...groups.values()]
        const max = Math.max(1, ...list.map(g => g.tripped))
        const rows = list.map(g => ({
          number: g.number,
          label: g.label,
          tripped: g.tripped,
          loans: g.loanIds.size,
          openNow: g.openNow,
          median: this.median(g.durations),
          mean: g.durations.length ? Math.round(g.durations.reduce((s, x) => s + x, 0) / g.durations.length) : null,
          longest: g.durations.length ? Math.max(...g.durations) : null,
          share: Math.round((g.tripped / max) * 100)
        }))
        return rows.sort((a, b) => this.compareRows(a, b, this.sorts.reasons))
      },
      exportRows() {
        if (this.tab === 'reasons') return this.reasonRows
        return this.listRows
      }
    },
    watch: {
      tab() {
        this.page = 1
        this.expandedId = null
      },
      search() { this.page = 1 },
      reasonFilter() { this.page = 1 },
      channelFilter() { this.page = 1 },
      branchFilter() { this.page = 1 },
      showInactive() { this.page = 1 },
      // A refresh can shorten the list under the current page (loans validated since).
      listRows() {
        if (this.page > this.totalPages) this.page = this.totalPages
      }
    },
    async mounted() {
      this.clock = setInterval(() => { this.now = Date.now() }, 30000)
      this.refreshTimer = setInterval(() => this.refreshQuietly(), REFRESH_MS)
      document.addEventListener('visibilitychange', this.onVisibilityChange)
      await this.loadData()
    },
    // Vue 3: beforeDestroy never runs here, so the timers and listener are cleared in beforeUnmount.
    beforeUnmount() {
      clearInterval(this.clock)
      clearInterval(this.refreshTimer)
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    },
    methods: {
      // `quiet` is the background refresh: no loading state, no jump back to page 1, and a failure
      // keeps the rows on screen with a note that they are stale. Only a literal true counts, since
      // the Refresh button and the days select pass their DOM event in as the first argument.
      async loadData(quiet) {
        const silent = quiet === true
        const seq = ++this.loadSeq
        this.loadInFlight = true

        if (!silent) {
          this.loading = true
          this.errorMessage = ''
        }

        try {
          const tokenResponse = await auth.acquireTokenResponse()
          const token = tokenResponse.accessToken

          const response = await api.GetLockValidations(this.days, token)
          if (seq !== this.loadSeq) return

          const payload = response?.data

          if (payload && payload.issuccess === false) {
            throw new Error(payload.message || 'Could not load validations.')
          }

          const data = payload?.data || {}
          const episodes = Array.isArray(data.episodes) ? data.episodes : []

          this.episodes = episodes.map(e => this.decorate(e))
          this.accessDenied = false
          this.errorMessage = ''
          this.loadedAt = new Date()
          this.now = Date.now()
          if (!silent) this.page = 1
        } catch (e) {
          if (seq !== this.loadSeq) return

          // 403 is the endpoint saying this user is not a ConcessionsAdmin: a refusal, not a
          // fault, so the not-authorized card rather than an error strip over an empty page.
          if (e?.response?.status === 403) {
            this.episodes = []
            this.accessDenied = true
            this.accessMessage = e?.response?.data?.message || 'You do not have access to Validations.'
          } else {
            const message = e?.response?.data?.message || e?.message || 'Could not load validations.'
            if (silent && this.loadedAt) {
              this.errorMessage = `Auto-refresh failed, so this is the data from ${this.formatDateTime(this.loadedAt)}: ${message}`
            } else {
              this.episodes = []
              this.errorMessage = message
            }
          }
        } finally {
          if (seq === this.loadSeq) {
            this.accessChecked = true
            this.loading = false
            this.loadInFlight = false
          }
        }
      },
      refreshQuietly() {
        if (document.hidden || this.loadInFlight || !this.isAuthorized) return
        this.loadData(true)
      },
      // Timers are throttled in a background tab, so coming back to one refreshes straight away
      // if the data has gone past a refresh interval.
      onVisibilityChange() {
        if (document.hidden || !this.loadedAt) return
        if (Date.now() - this.loadedAt.getTime() >= REFRESH_MS) this.refreshQuietly()
      },
      // Everything the tables, filters and sorts need, computed once per episode.
      decorate(raw) {
        const ep = { ...raw }
        const opened = this.toDate(raw.opened_utc)
        const closed = this.toDate(raw.closed_utc)
        const lockExpires = this.toDay(raw.lock_expires)

        ep.openedMs = opened ? opened.getTime() : null
        ep.closedMs = closed ? closed.getTime() : null
        ep.isOpen = !raw.closed_utc
        ep.lockExpiresMs = lockExpires ? lockExpires.getTime() : null
        // A lock is good through its expiration date, so it has expired once that day is over.
        ep.lockExpired = ep.isOpen && ep.lockExpiresMs !== null && ep.lockExpiresMs + 86400000 <= Date.now()
        ep.borrowerName = [raw.borrower_first, raw.borrower_last].filter(Boolean).join(' ').trim()
        // A loan missing from MasterReporting has no status; that is unknown, not inactive.
        ep.isInactive = !!raw.loan_status && raw.loan_status.trim().toLowerCase() !== 'active loan'

        ep.reasons = (Array.isArray(raw.reasons) ? raw.reasons : []).map(r => {
          const tripped = this.toDate(r.tripped_utc)
          return {
            ...r,
            trippedMs: tripped ? tripped.getTime() : null,
            isOpen: !r.cleared_utc
          }
        })
        ep.reasonCount = ep.reasons.length

        ep.searchText = [
          raw.loan_number, ep.borrowerName, raw.loan_officer_name, raw.orgid, raw.channel,
          ...ep.reasons.map(r => r.reason_label)
        ].filter(Boolean).join(' ').toLowerCase()

        return ep
      },
      filterRows(rows, isOpenTab) {
        const q = this.search.toLowerCase()
        return rows.filter(row => {
          if (isOpenTab && !this.showInactive && row.isInactive) return false
          if (this.reasonFilter && !row.reasons.some(r => r.reason_number === this.reasonFilter)) return false
          if (this.channelFilter && row.channel !== this.channelFilter) return false
          if (this.branchFilter && row.orgid !== this.branchFilter) return false
          if (q && !row.searchText.includes(q)) return false
          return true
        })
      },
      // On the open tab, the reasons tripped right now first, then the ones that already cleared.
      rowReasons(row) {
        if (this.tab !== 'open') return row.reasons
        return [...row.reasons.filter(r => r.isOpen), ...row.reasons.filter(r => !r.isOpen)]
      },
      otherEpisodes(row) {
        return (this.episodesByLoan.get(row.loan_id) || []).filter(ep => ep.id !== row.id)
      },
      // ── Durations ───────────────────────────────────────────────────────────
      episodeSeconds(ep) {
        if (!ep.isOpen) return ep.duration_seconds
        if (ep.openedMs === null) return null
        return Math.max(0, Math.round((this.now - ep.openedMs) / 1000))
      },
      reasonSeconds(r) {
        if (!r.isOpen) return r.duration_seconds
        if (r.trippedMs === null) return null
        return Math.max(0, Math.round((this.now - r.trippedMs) / 1000))
      },
      // "3h 20m", or "≥ 3h 20m" when the start is unknown and the figure is only a floor.
      ageText(ep) {
        const text = this.fmtDuration(this.episodeSeconds(ep))
        return !ep.start_known && text !== '—' ? '≥ ' + text : text
      },
      fmtDuration(seconds) {
        return formatDuration(seconds)
      },
      median(values) {
        if (!values.length) return null
        const list = [...values].sort((a, b) => a - b)
        const mid = Math.floor(list.length / 2)
        return list.length % 2 ? list[mid] : Math.round((list[mid - 1] + list[mid]) / 2)
      },
      // Nearest-rank: the smallest value at or above the given share of the list.
      percentile(values, p) {
        if (!values.length) return null
        const list = [...values].sort((a, b) => a - b)
        return list[Math.min(list.length - 1, Math.ceil(p * list.length) - 1)]
      },
      // ── Sorting ─────────────────────────────────────────────────────────────
      sort(field) {
        const s = this.sorts[this.tab]
        if (s.by === field) {
          s.dir = s.dir === 'asc' ? 'desc' : 'asc'
        } else {
          s.by = field
          s.dir = ['closedMs', 'seconds', 'reasonCount', 'tripped', 'loans', 'openNow', 'median', 'mean', 'longest'].includes(field)
            ? 'desc'
            : 'asc'
        }
        this.page = 1
      },
      sortMark(field) {
        const s = this.sorts[this.tab]
        if (s.by !== field) return ''
        return s.dir === 'asc' ? '▲' : '▼'
      },
      compareRows(a, b, s = this.sorts[this.tab]) {
        let aVal = a[s.by]
        let bVal = b[s.by]

        if (s.by === 'seconds') {
          aVal = this.episodeSeconds(a)
          bVal = this.episodeSeconds(b)
        }

        if (aVal === bVal) return 0
        // Unknowns sort last in both directions: they are absence, not a low value.
        if (aVal === null || aVal === undefined || aVal === '') return 1
        if (bVal === null || bVal === undefined || bVal === '') return -1

        if (typeof aVal === 'number' && typeof bVal === 'number') {
          return s.dir === 'asc' ? aVal - bVal : bVal - aVal
        }

        const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
        return s.dir === 'asc' ? result : -result
      },
      toggleExpand(row) {
        this.expandedId = this.expandedId === row.id ? null : row.id
      },
      // ── Dates ───────────────────────────────────────────────────────────────
      // The API sends UTC with a "Z". Guarded anyway, as the lock desk queue does, so a bare
      // timestamp is still read as UTC rather than as the browser's local time.
      toDate(value) {
        if (!value) return null
        if (value instanceof Date) return value
        const str = typeof value === 'string' && !value.endsWith('Z') && !/[+-]\d\d:\d\d$/.test(value)
          ? value + 'Z'
          : value
        const date = new Date(str)
        return isNaN(date.getTime()) ? null : date
      },
      // lock_expires is a calendar date from MasterReporting with no zone. Read as local, NOT
      // through toDate: as UTC midnight it would print as the day before in Eastern time.
      toDay(value) {
        if (!value) return null
        const date = new Date(String(value).replace(/Z$/, ''))
        return isNaN(date.getTime()) ? null : date
      },
      formatDay(value) {
        const date = this.toDay(value)
        if (!date) return '—'
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
      },
      formatDate(value) {
        const date = this.toDate(value)
        if (!date) return '—'
        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' })
      },
      formatDateTime(value) {
        const date = this.toDate(value)
        if (!date) return '—'
        return date.toLocaleString('en-US', {
          month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit'
        })
      },
      // ── Loan links ──────────────────────────────────────────────────────────
      pollyUrl(row) {
        return `https://lx.pollyex.com/pe/loans/${row.loan_id}?mode=lock&orgId=${this.pollyOrgId}`
      },
      encompassUrl(row) {
        return `https://www.encompassloconnect.com/pipeline/${row.loan_id}`
      },
      openLoanLink(url, event) {
        if (openLoanInWindow(url, event)) event.preventDefault()
      },
      // ── Export ──────────────────────────────────────────────────────────────
      // Whatever the current tab shows, filters applied.
      exportCsv() {
        const cell = value => {
          const text = value === null || value === undefined ? '' : String(value)
          return `"${text.replace(/"/g, '""')}"`
        }

        let headers
        let lines

        if (this.tab === 'reasons') {
          headers = ['Reason #', 'Reason', 'Times Tripped', 'Loans', 'Open Now',
            'Median Tripped (seconds)', 'Average (seconds)', 'Longest (seconds)']
          lines = this.reasonRows.map(r => [
            r.number, r.label, r.tripped, r.loans, r.openNow, r.median, r.mean, r.longest
          ])
        } else {
          headers = ['Loan Number', 'Borrower', 'Loan Officer', 'Branch', 'Channel', 'Loan Status',
            'Milestone', 'Unvalidated (UTC)', 'Start Known', 'Validated (UTC)',
            'Seconds Unvalidated', 'Reasons', 'Lock Expires']
          lines = this.listRows.map(row => [
            row.loan_number,
            row.borrowerName,
            row.loan_officer_name,
            row.orgid,
            row.channel,
            row.loan_status,
            row.current_milestone,
            row.opened_utc,
            row.start_known ? 'yes' : 'no',
            row.closed_utc,
            this.episodeSeconds(row),
            row.reasons.map(r => r.reason_label).join('; '),
            row.lock_expires
          ])
        }

        const csv = [headers, ...lines].map(line => line.map(cell).join(',')).join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')

        link.href = url
        link.download = this.tab === 'open'
          ? 'validations-open.csv'
          : `validations-${this.tab}-${this.days}d.csv`
        link.click()

        URL.revokeObjectURL(url)
      }
    }
  }
</script>

<style scoped>
  /* Same shell as the other lock desk dashboards: the leftover of #app's flex column, scrolling
     inside the page rather than around the whole app. */
  .lock-validations-page {
    padding: 18px;
    background: #f7f8fb;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: #1f2a44;
  }

  .page-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex: 0 0 auto;
  }

  .page-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .page-icon {
    width: 48px;
    height: 48px;
    border-radius: 5px;
    background: #123a71;
    color: #ffffff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex: 0 0 auto;
  }

  .page-title {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #1f2a44;
    letter-spacing: -0.01em;
  }

  .page-subtitle {
    margin: 4px 0 0;
    font-size: 13px;
    color: #7b8397;
    font-weight: 500;
  }

  .header-actions {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .tab-bar {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #e4e9f2;
    flex: 0 0 auto;
  }

  .tab-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    border: 0;
    border-bottom: 2px solid transparent;
    background: transparent;
    color: #7b8397;
    font-size: 13.5px;
    font-weight: 700;
    padding: 8px 14px;
    margin-bottom: -1px;
    cursor: pointer;
  }

  .tab-btn:hover {
    color: #43567d;
  }

  .tab-btn.is-on {
    color: #123a71;
    border-bottom-color: #123a71;
  }

  .tab-btn-count {
    background: #eef1f7;
    color: #6f7b92;
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 11.5px;
    font-weight: 700;
  }

  .tab-btn.is-on .tab-btn-count {
    background: #eef4ff;
    color: #2f5798;
  }

  .page-error {
    padding: 10px 12px;
    border-radius: 5px;
    background: #fff5f5;
    border: 1px solid #f3d4d8;
    color: #a4303c;
    font-size: 13px;
    font-weight: 600;
  }

  .panel-hint {
    display: flex;
    align-items: flex-start;
    gap: 9px;
    padding: 10px 12px;
    border-radius: 5px;
    background: #f5f8fc;
    border: 1px solid #dde5f2;
    color: #395583;
    font-size: 12.5px;
    line-height: 1.6;
    flex: 0 0 auto;
  }

  .panel-hint i {
    margin-top: 3px;
  }

  /* ── Overview cards ── */
  .stat-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
    gap: 10px;
    flex: 0 0 auto;
  }

  .stat-card {
    background: #fdfdfe;
    border: 1px solid #e8ebf2;
    border-radius: 5px;
    padding: 12px;
    min-width: 0;
  }

  .stat-card.stat-muted {
    opacity: 0.6;
  }

  .stat-head {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }

  .stat-icon {
    font-size: 13px;
    color: #6a7b9f;
  }

  .stat-label {
    font-size: 12px;
    color: #7b8397;
    font-weight: 600;
  }

  .stat-value {
    font-size: 22px;
    font-weight: 800;
    color: #1f2a44;
    line-height: 1.1;
  }

  /* A reason name, not a number: smaller, and allowed to wrap rather than overflow the card. */
  .stat-value-text {
    font-size: 16px;
    line-height: 1.3;
    word-break: break-word;
  }

  .stat-foot {
    margin-top: 6px;
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 500;
  }

  /* ── Panels ── */
  .panel {
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 5px;
    padding: 12px;
  }

  .table-panel {
    flex: 0 0 auto;
  }

  .panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
    gap: 10px;
  }

  .panel-title-wrap {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .panel-icon {
    color: #5f79c7;
    font-size: 14px;
  }

  .panel-title {
    font-size: 14px;
    font-weight: 700;
    color: #28344f;
  }

  .panel-note {
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 500;
  }

  .panel-count {
    font-size: 13px;
    font-weight: 700;
    color: #7b8397;
  }

  .sub-title {
    font-size: 12px;
    font-weight: 700;
    color: #5c6780;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 8px;
  }

  /* ── Toolbar ── */
  .toolbar {
    display: grid;
    grid-template-columns: minmax(240px, 1fr) 210px 170px 150px auto;
    gap: 8px;
    margin-bottom: 10px;
    align-items: center;
  }

  .search-input,
  .filter-select {
    height: 38px;
    border: 1px solid #dfe4ee;
    background: #fcfcfe;
    border-radius: 5px;
    padding: 0 14px;
    font-size: 13px;
    width: 100%;
    color: #23314f;
    outline: none;
  }

  .search-input::placeholder {
    color: #94a0b8;
  }

  .search-input:focus,
  .filter-select:focus {
    border-color: #cfd8e8;
    box-shadow: 0 0 0 2px rgba(49, 93, 181, 0.08);
  }

  .days-select {
    min-width: 190px;
  }

  .toggle-inline {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12.5px;
    font-weight: 600;
    color: #5c6780;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
  }

  .toggle-count {
    background: #eef1f7;
    color: #6f7b92;
    border-radius: 999px;
    padding: 1px 8px;
    font-size: 11px;
    font-weight: 700;
  }

  /* ── Buttons ── */
  .refresh-btn,
  .export-btn,
  .page-btn,
  .polly-btn,
  .encompass-btn {
    border: 1px solid #d9dfeb;
    border-radius: 5px;
    background: #ffffff;
    color: #22314f;
    height: 38px;
    padding: 0 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    text-decoration: none;
  }

  .refresh-btn:hover,
  .export-btn:hover,
  .page-btn:hover,
  .polly-btn:hover,
  .encompass-btn:hover {
    background: #f4f7fc;
    border-color: #cfd8e8;
  }

  .export-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  .refresh-btn:disabled,
  .export-btn:disabled,
  .page-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .polly-btn,
  .encompass-btn {
    height: 30px;
    padding: 0 12px;
    font-size: 12px;
  }

  .polly-btn {
    color: #0f766e;
    background: #f3fbf9;
    border-color: #cfe9e2;
  }

  .encompass-btn {
    color: #315db5;
    background: #f5f8fc;
    border-color: #d9e2f0;
  }

  /* ── Tables ── */
  /* A definite height makes this wrapper the scroll port, which is what keeps the sticky header
     attached to its own rows rather than floating under the app nav. */
  .table-wrap {
    overflow: auto;
    max-height: 62vh;
    border: 1px solid #edf0f5;
    border-radius: 5px;
    background: #fdfdfe;
  }

  .table-wrap > .data-table > thead th {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  .data-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
  }

  .loans-table {
    min-width: 1300px;
  }

  .data-table th,
  .data-table td {
    padding: 10px 12px;
    border-bottom: 1px solid #edf0f5;
    font-size: 12.5px;
    text-align: left;
    vertical-align: middle;
  }

  .data-table th {
    background: #f7f9fc;
    color: #6f7b92;
    font-weight: 700;
    white-space: nowrap;
  }

  .sortable-table > thead th {
    cursor: pointer;
  }

  .data-table td.num,
  .data-table th.num {
    text-align: right;
  }

  .data-table .strong,
  .strong {
    font-weight: 700;
    color: #24314f;
  }

  .data-table .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
  }

  .muted {
    color: #8a93a8;
    font-weight: 500;
  }

  .table-state {
    padding: 22px;
    text-align: center;
    color: #8a93a8;
    font-size: 13px;
  }

  .loan-row {
    cursor: pointer;
  }

  .loan-row:hover {
    background: #f8faff;
  }

  .loan-row.is-open {
    background: #f4f7fd;
  }

  .expand-col {
    width: 28px;
    color: #98a1b5;
  }

  .lock-expired {
    color: #a4302a;
    font-weight: 700;
  }

  /* ── Pills and chips ── */
  .reason-pill,
  .since-chip,
  .status-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    white-space: nowrap;
    border: 1px solid transparent;
    margin: 1px 4px 1px 0;
  }

  .reason-pill {
    background: #fdecec;
    color: #a4302a;
    border-color: #f4c4c1;
  }

  .reason-pill.reason-cleared {
    background: #f5f6f8;
    color: #8a93a8;
    border-color: #e6e9ef;
    text-decoration: line-through;
  }

  .reason-pill.reason-none {
    background: #f5f6f8;
    color: #8a93a8;
    border-color: #e6e9ef;
  }

  .reason-more {
    color: #7b8397;
    font-size: 11px;
    font-weight: 700;
  }

  .since-chip {
    background: #fff7ed;
    color: #8a5314;
    border-color: #f3d9b0;
    margin-left: 4px;
  }

  .status-chip {
    background: #f2f4f7;
    color: #6d7789;
    border-color: #e1e5ec;
    margin-left: 4px;
  }

  .reason-number {
    display: inline-block;
    min-width: 22px;
    color: #98a1b5;
    font-weight: 700;
    font-size: 11.5px;
  }

  /* ── By reason bars ── */
  .bar-cell {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 220px;
  }

  .mix-bar {
    flex: 1;
    height: 8px;
    border-radius: 999px;
    background: #eef1f7;
    overflow: hidden;
  }

  .mix-bar-fill {
    height: 100%;
    border-radius: 999px;
    background: #c05650;
  }

  .bar-count {
    min-width: 32px;
    text-align: right;
    font-weight: 700;
    color: #1f2a44;
  }

  /* ── Expanded detail ── */
  .detail-row > td {
    background: #f9fbff;
    padding: 14px 16px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr);
    gap: 18px;
  }

  .detail-block {
    min-width: 0;
  }

  .detail-empty {
    font-size: 12.5px;
    color: #8a93a8;
    line-height: 1.5;
  }

  .reason-table {
    border: 1px solid #edf0f5;
    border-radius: 5px;
    background: #fdfdfe;
  }

  .reason-table th,
  .reason-table td {
    padding: 7px 10px;
    font-size: 12px;
  }

  .value-old {
    color: #8a93a8;
  }

  .value-arrow {
    font-size: 10px;
    color: #98a1b5;
    margin: 0 6px;
  }

  .value-new {
    color: #24314f;
    font-weight: 700;
  }

  .detail-list {
    margin: 0;
  }

  .detail-list > div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 4px 0;
    border-bottom: 1px solid #edf1f8;
  }

  .detail-list dt {
    font-size: 11.5px;
    color: #8a93a8;
    font-weight: 600;
    flex: 0 0 auto;
  }

  .detail-list dd {
    margin: 0;
    font-size: 12.5px;
    color: #24314f;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }

  .detail-links {
    display: flex;
    gap: 8px;
    margin-top: 12px;
  }

  /* ── Pagination ── */
  .pagination-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px 4px 0;
    gap: 12px;
    flex: 0 0 auto;
  }

  .pagination-info {
    color: #7b8397;
    font-size: 12.5px;
    font-weight: 500;
  }

  .pagination-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .page-number {
    font-size: 13px;
    color: #5c6780;
    font-weight: 600;
  }

  .state-card {
    margin: auto;
    padding: 28px 36px;
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 12px;
    color: #7b8397;
    font-size: 13px;
  }

  /* ── Not authorized ── */
  .not-authorized-card {
    margin: auto;
    text-align: center;
    background: #fbfbfd;
    border: 1px solid #e7ebf2;
    border-radius: 12px;
    padding: 40px 48px;
  }

  .not-authorized-icon {
    font-size: 28px;
    color: #98a1b5;
    margin-bottom: 10px;
  }

  .not-authorized-title {
    font-size: 17px;
    font-weight: 700;
    color: #1f2a44;
  }

  .not-authorized-text {
    margin-top: 6px;
    font-size: 13px;
    color: #7b8397;
  }

  @media (max-width: 1100px) {
    .detail-grid {
      grid-template-columns: minmax(0, 1fr);
    }

    .toolbar {
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    }
  }
</style>
